import { queryOptions, useQuery } from '@tanstack/react-query';

import { getGetContextUsageQueryKey } from '@/api/gen/context-usage';
import type { TLContextUsageReport } from '@/api/gen/tessera-backend-api.schemas';
import { getApiClient } from '@/utils/api-client';
import type { QueryOptions } from '@/utils/types';

// Re-export the generated types/enums so feature code has a single import path for this endpoint,
// instead of reaching into `@/api/gen` directly and duplicating (and drifting from) their shape.
export type {
  TLCompositionReport,
  TLContextUsageCategory,
  TLContextUsageItem,
  TLContextUsageReport,
  TLContextUsageTurnGroup,
} from '@/api/gen/tessera-backend-api.schemas';
export {
  TLContextUsageCategoryId,
  TLContextUsageRole,
  TLContextUsageStatus,
} from '@/api/gen/tessera-backend-api.schemas';

/** Stable query key; reuses orval's generated key so it can't drift from what the generated hooks
 * use, and so it's correctly invalidated from the SSE `complete` handler and mutation
 * `invalidateQueries` lists (see use-message-stream.ts, conversations.ts). */
export const contextUsageQueryKey = (conversationId: string) => getGetContextUsageQueryKey({ conversationId });

export const contextUsageQueryOptions = (conversationId: string, options?: QueryOptions<TLContextUsageReport>) =>
  queryOptions({
    queryKey: contextUsageQueryKey(conversationId),
    queryFn: async () => {
      return (await getApiClient()).get<TLContextUsageReport>('/conversations', {
        urlParams: { path: [conversationId, 'context-usage'] },
      });
    },
    enabled: !!conversationId,
    // The report is a live recompute, not persisted; treat it as short-lived.
    staleTime: 10_000,
    ...options,
  });

export const useContextUsageQuery = (conversationId: string, options?: QueryOptions<TLContextUsageReport>) => {
  return useQuery(contextUsageQueryOptions(conversationId, options));
};
